import React, { useContext, useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ModalOverlay,
  DocumentContainer,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ScrollableContent,
  SectionTitle,
  InfoGrid,
  InfoItem,
  InfoItemWithTooltip,
  InfoValueContainer,
  InfoLink,
  InfoLabel,
  InfoValue,
  ItemCard,
  ItemHeader,
  ItemContent,
  ServicesList,
  ServiceItem,
  AttachmentsList,
  AttachmentItem,
  MessageContainer,
  ErrorMessage,
  Table,
  TableHeaderCell,
  TableCell,
  TableRow,
} from './style'
import { Info } from 'lucide-react'
import { fetchOrderById } from './api'
import { OrderSchemaType } from '../AddOrder/order.schema'
import { paymentMethodLabel } from '../AddOrder/constants'
import ModalContext from '../../../../contexts/ModalContext'
import InfoTooltip from '../../../../components/InfoTooltip'

const ViewSteelOrder: React.FC = () => {
  const { setModalActive } = useContext(ModalContext)
  const { orderId } = useParams<{ orderId: string }>()
  const navigate = useNavigate()
  const [activeInfo, setActiveInfo] = useState<string | null>(null)
  const [collapsedItems, setCollapsedItems] = useState<{
    [key: number]: boolean
  }>({})

  const {
    data: order,
    isLoading,
    isError,
    error,
  } = useQuery<OrderSchemaType>({
    queryKey: ['steelOrder', orderId],
    queryFn: () => fetchOrderById(orderId!),
    enabled: !!orderId,
  })

  useEffect(() => {
    if (order?.items) {
      const initialCollapsedState: { [key: number]: boolean } = {}
      order.items.forEach((_, index) => {
        initialCollapsedState[index] = true
      })
      setCollapsedItems(initialCollapsedState)
    }
  }, [order])

  const handleClose = () => {
    setModalActive(false)
    navigate('/panel/steel')
  }

  const handleInfoToggle = (type: string, hasDetails: boolean) => {
    if (!hasDetails) return
    setActiveInfo(activeInfo === type ? null : type)
  }

  const toggleItemCollapsed = (index: number) => {
    setCollapsedItems((prev) => ({
      ...prev,
      [index]: !prev[index],
    }))
  }

  const getItemTypeCounts = (item: any) => {
    const counts = { P: 0, S: 0, I: 0, M: 0 }

    if (item.itemType === 'PRODUCT') counts.P = 1
    if (item.itemType === 'SERVICE') counts.S = 1
    if (item.itemType === 'RESOURCE') counts.I = 1
    if (item.itemType === 'MATERIAL') counts.M = 1

    if (item.subItems && item.subItems.length > 0) {
      item.subItems.forEach((subItem: any) => {
        if (subItem.itemType === 'PRODUCT') counts.P++
        if (subItem.itemType === 'SERVICE') counts.S++
        if (subItem.itemType === 'RESOURCE') counts.I++
        if (subItem.itemType === 'MATERIAL') counts.M++
      })
    }

    return counts
  }

  const buildAddressString = (entity: any): string => {
    const safe = (v: any): string => {
      if (!v && v !== 0) return '';
      if (typeof v === 'string') return v;
      if (typeof v === 'number') return String(v);
      if (typeof v === 'object' && v !== null) return v.name || v.label || '';
      return '';
    };
    const street = safe(entity.street);
    const number = safe(entity.number);
    const neighborhood = safe(entity.neighborhood);
    const city = safe(entity.city);
    const state = safe(entity.state);
    const rawCep = entity.cep || '';
    const cepDigits = String(rawCep).replace(/\D/g, '');
    const cepPadded = cepDigits ? cepDigits.padStart(8, '0') : '';
    const cepFormatted = cepPadded ? `${cepPadded.slice(0, 5)}-${cepPadded.slice(5)}` : '';
    const parts: string[] = [];
    if (street) parts.push(`${street}${number ? `, ${number}` : ''}`);
    if (neighborhood) parts.push(neighborhood);
    if (city) parts.push(city);
    if (state) parts.push(state);
    const main = parts.join(' - ');
    const result = cepFormatted ? `${main}${main ? ' - ' : ''}${cepFormatted}` : main;
    return result.trim();
  };

  const getSupplierDetails = () => {
    if (!order?.supplier || order.supplier === 'Não informado') return undefined
    const supplier = order?.supplierDetails
    if (!supplier) return undefined
    const address = buildAddressString(supplier)
    const tipo = supplier.isMain ? (supplier.tipoEstabelecimento || (supplier.cnpj ? (supplier.cnpj.replace(/\D/g, '').substring(8, 12) === '0001' ? 'Matriz' : 'Filial') : '')) : '';
    return {
      'Razão Social': supplier.companyName || supplier.name || 'Não informado',
      ...(tipo ? { 'Tipo': tipo } : {}),
      'CNPJ/CPF': supplier.cnpj || supplier.cpf || 'Não informado',
      'Nome do Contato': supplier.contactName || supplier.contato || (supplier.contacts?.[0]?.name) || 'Não informado',
      'Email': supplier.email || (supplier.contacts?.[0]?.email) || 'Não informado',
      'Telefone': supplier.phone || supplier.telefone || (supplier.contacts?.[0]?.phoneNumber) || 'Não informado',
      ...(address ? { 'Endereço': address } : {}),
    }
  }

  const getSellerDetails = () => {
    if (!order?.seller || order.seller === 'Não informado') return undefined
    const seller = order?.sellerDetails
    if (!seller) return undefined
    return {
      'Nome': seller.name || 'Não informado',
      'Email': seller.email || 'Não informado',
      'Telefone': seller.phone || seller.telefone || 'Não informado',
    }
  }

  const getClosedByDetails = () => {
    if (!order?.closedBy || order.closedBy === 'Não informado') return undefined
    const closedBy = order?.closedByDetails
    if (!closedBy) return undefined
    return {
      'Nome': closedBy.name || 'Não informado',
      'Email': closedBy.email || 'Não informado',
      'Telefone': closedBy.phone || closedBy.telefone || 'Não informado',
    }
  }

  const getClientDetails = () => {
    if (!order?.client || order.client === 'Não informado') return undefined
    const client = order?.clientDetails
    if (!client) return undefined
    const address = buildAddressString(client)
    const tipo = client.isMain ? (client.tipoEstabelecimento || (client.cnpj ? (client.cnpj.replace(/\D/g, '').substring(8, 12) === '0001' ? 'Matriz' : 'Filial') : '')) : '';
    return {
      'Razão Social': client.companyName || client.name || 'Não informado',
      ...(tipo ? { 'Tipo': tipo } : {}),
      'CNPJ/CPF': client.cnpj || client.cpf || 'Não informado',
      'Nome do Contato': client.contactName || client.contato || (client.contacts?.[0]?.name) || 'Não informado',
      'Email': client.email || (client.contacts?.[0]?.email) || 'Não informado',
      'Telefone': client.phone || client.telefone || (client.contacts?.[0]?.phoneNumber) || 'Não informado',
      ...(address ? { 'Endereço': address } : {}),
    }
  }

  const formatCurrency = (value?: number) => {
    if (!value) return 'R$ 0,00'
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value)
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Não informado'
    return new Date(dateString).toLocaleDateString('pt-BR')
  }

  const getSupplierNameWithLabel = () => {
    if (!order) return 'Não informado';
    if (!order.supplier || order.supplier === 'Não informado') return 'Não informado';
    const supplier = order.supplierDetails;
    if (!supplier) return order.supplier;
    const tipo = supplier.isMain ? (supplier.tipoEstabelecimento || (supplier.cnpj ? (supplier.cnpj.replace(/\D/g, '').substring(8, 12) === '0001' ? 'Matriz' : 'Filial') : '')) : '';
    return tipo ? `${order.supplier} - ${tipo}` : order.supplier;
  };

  const getClientNameWithLabel = () => {
    if (!order) return 'Não informado';
    if (!order.client || order.client === 'Não informado') return 'Não informado';
    const client = order.clientDetails;
    if (!client) return order.client;
    const tipo = client.isMain ? (client.tipoEstabelecimento || (client.cnpj ? (client.cnpj.replace(/\D/g, '').substring(8, 12) === '0001' ? 'Matriz' : 'Filial') : '')) : '';
    return tipo ? `${order.client} - ${tipo}` : order.client;
  };

  if (isLoading) {
    return (
      <DocumentContainer>
        <MessageContainer>Carregando detalhes do pedido...</MessageContainer>
      </DocumentContainer>
    )
  }

  if (isError) {
    return (
      <DocumentContainer>
        <ErrorMessage>
          Falha ao carregar o pedido. Erro:{' '}
          {error?.message || 'Erro desconhecido'}
        </ErrorMessage>
      </DocumentContainer>
    )
  }

  return (
    <ModalOverlay onClick={handleClose}>
      <DocumentContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Detalhes do Pedido - Nº {order?.id}</ModalTitle>
          <CloseButton onClick={handleClose}>&times;</CloseButton>
        </ModalHeader>
        <ScrollableContent>
          <SectionTitle>Informações Gerais</SectionTitle>
          <InfoGrid>
            <InfoItemWithTooltip>
              <InfoLabel>Fornecedor:</InfoLabel>
              <InfoValueContainer>
                <InfoValue>{getSupplierNameWithLabel()}</InfoValue>
                <InfoLink
                  disabled={
                    !order?.supplier || order.supplier === 'Não informado'
                  }
                  onClick={() =>
                    handleInfoToggle(
                      'supplier',
                      !!(order?.supplier && order.supplier !== 'Não informado'),
                    )
                  }
                >
                  <Info size={16} />
                  <span>Mais informações</span>
                </InfoLink>
              </InfoValueContainer>
              {activeInfo === 'supplier' &&
                (getSupplierDetails() ? (
                  <InfoTooltip details={getSupplierDetails()!} />
                ) : (
                  <ErrorMessage>
                    Não foi possível encontrar os detalhes do fornecedor.
                  </ErrorMessage>
                ))}
            </InfoItemWithTooltip>

            <InfoItemWithTooltip>
              <InfoLabel>Vendedor:</InfoLabel>
              <InfoValueContainer>
                <InfoValue>{order?.seller || 'Não informado'}</InfoValue>
                <InfoLink
                  disabled={!order?.seller || order.seller === 'Não informado'}
                  onClick={() =>
                    handleInfoToggle(
                      'seller',
                      !!(order?.seller && order.seller !== 'Não informado'),
                    )
                  }
                >
                  <Info size={16} />
                  <span>Mais informações</span>
                </InfoLink>
              </InfoValueContainer>
              {activeInfo === 'seller' &&
                (getSellerDetails() ? (
                  <InfoTooltip details={getSellerDetails()!} />
                ) : (
                  <ErrorMessage>
                    Não foi possível encontrar os detalhes do vendedor.
                  </ErrorMessage>
                ))}
            </InfoItemWithTooltip>

            <InfoItemWithTooltip>
              <InfoLabel>Cliente:</InfoLabel>
              <InfoValueContainer>
                <InfoValue>{getClientNameWithLabel()}</InfoValue>
                <InfoLink
                  disabled={!order?.client || order.client === 'Não informado'}
                  onClick={() =>
                    handleInfoToggle(
                      'client',
                      !!(order?.client && order.client !== 'Não informado'),
                    )
                  }
                >
                  <Info size={16} />
                  <span>Mais informações</span>
                </InfoLink>
              </InfoValueContainer>
              {activeInfo === 'client' &&
                (getClientDetails() ? (
                  <InfoTooltip details={getClientDetails()!} />
                ) : (
                  <ErrorMessage>
                    Não foi possível encontrar os detalhes do cliente.
                  </ErrorMessage>
                ))}
            </InfoItemWithTooltip>

            {order?.status === true && order?.closedBy && (
              <InfoItemWithTooltip>
                <InfoLabel>Fechado por:</InfoLabel>
                <InfoValueContainer>
                  <InfoValue>{order.closedBy}</InfoValue>
                  <InfoLink
                    disabled={!order.closedBy || order.closedBy === 'Não informado'}
                    onClick={() =>
                      handleInfoToggle(
                        'closedBy',
                        !!(order.closedBy && order.closedBy !== 'Não informado'),
                      )
                    }
                  >
                    <Info size={16} />
                    <span>Mais informações</span>
                  </InfoLink>
                </InfoValueContainer>
                {activeInfo === 'closedBy' &&
                  (getClosedByDetails() ? (
                    <InfoTooltip details={getClosedByDetails()!} />
                  ) : (
                    <ErrorMessage>
                      Não foi possível encontrar os detalhes do usuário.
                    </ErrorMessage>
                  ))}
              </InfoItemWithTooltip>
            )}

            <InfoItem>
              <InfoLabel>Tipo de Pedido:</InfoLabel>
              <InfoValue>{order?.orderType || 'Não informado'}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Data do Pedido:</InfoLabel>
              <InfoValue>{formatDate(order?.orderDate)}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Data de Entrega:</InfoLabel>
              <InfoValue>{formatDate(order?.deliveryDate)}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Condição de Pagamento:</InfoLabel>
              <InfoValue>
                {(order as any)?.PaymentCondition?.name || (order as any)?.paymentCondition?.name || 'Não informado'}
              </InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Forma de Pagamento:</InfoLabel>
              <InfoValue>
                {paymentMethodLabel((order as any)?.OrderPaymentMethod?.[0]?.PaymentMethod?.name) || 'Não informado'}
              </InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Tipo de Frete:</InfoLabel>
              <InfoValue>
                {(order as any)?.ShippingType?.name || (order as any)?.shippingType?.name || 'Não informado'}
              </InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Valor do Frete:</InfoLabel>
              <InfoValue>{formatCurrency(order?.shippingValue)}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Código de Rastreamento:</InfoLabel>
              <InfoValue>{order?.trackingCode || 'Não informado'}</InfoValue>
            </InfoItem>
          </InfoGrid>

          {order?.extraInfo && (
            <>
              <div style={{ marginBottom: '50px' }}>
                <SectionTitle>Informações Adicionais</SectionTitle>
                <div style={{ marginTop: '40px' }}>
                  <InfoValue className='extra-info-block'>
                    {order.extraInfo}
                  </InfoValue>
                </div>
              </div>
            </>
          )}

          {order?.attachments && order.attachments.length > 0 && (
            <>
              <div style={{ marginTop: '25px' }}>
                <SectionTitle>Anexos</SectionTitle>
                <AttachmentsList>
                  {order.attachments.map((attachment, index) => (
                    <AttachmentItem key={index}>📎 {attachment}</AttachmentItem>
                  ))}
                </AttachmentsList>
              </div>
            </>
          )}

          <SectionTitle>Itens do Pedido</SectionTitle>
          {order?.items?.map((item: any, index: number) => {
            const getItemTitle = () => {
              if (item.itemType === 'MATERIAL' && item.material?.name) {
                return item.material.name
              }
              if (item.itemType === 'PRODUCT' && item.product?.name) {
                return item.product.name
              }
              if (item.itemType === 'SERVICE' && item.stage?.name) {
                return item.stage.name
              }
              if (item.itemType === 'RESOURCE' && item.resource?.name) {
                return item.resource.name
              }
              return `${item.itemType} ${index + 1}`
            }

            const getItemSubtitle = () => {
              if (item.itemType === 'SERVICE' && item.serviceCategory?.name) {
                return ` - ${item.serviceCategory.name}`
              }
              if (item.itemType === 'RESOURCE' && item.resourceCategory?.name) {
                return ` - ${item.resourceCategory.name}`
              }
              if (item.itemType === 'MATERIAL' && item.stock?.description) {
                return ` - ${item.stock.description}`
              }
              return ''
            }

            const getItemTypeLabel = () => {
              const typeMap: { [key: string]: string } = {
                MATERIAL: 'Material',
                PRODUCT: 'Produto',
                SERVICE: 'Serviço',
                RESOURCE: 'Insumo',
              }
              return typeMap[item.itemType] || item.itemType
            }

            return (
              <ItemCard key={index}>
                <ItemHeader
                  onClick={() => toggleItemCollapsed(index)}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '15px',
                        color: 'white',
                      }}
                    >
                      <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
                        Item Principal:
                      </span>
                      <span style={{ fontSize: '14px' }}>
                        {getItemTypeLabel()}
                      </span>
                      <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
                        Quantidade de produtos:
                      </span>
                      <span style={{ fontSize: '14px' }}>
                        {1 + (item.subItems?.length || 0)}
                      </span>
                      <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
                        Totais por tipo de produto:
                      </span>
                      {(() => {
                        const counts = getItemTypeCounts(item)
                        const typeLabels = []
                        if (counts.P > 0) typeLabels.push(`Produto:${counts.P}`)
                        if (counts.S > 0) typeLabels.push(`Serviço:${counts.S}`)
                        if (counts.I > 0) typeLabels.push(`Insumo:${counts.I}`)
                        if (counts.M > 0)
                          typeLabels.push(`Material:${counts.M}`)

                        if (typeLabels.length === 0) return null
                        if (typeLabels.length === 1)
                          return (
                            <span style={{ fontSize: '14px' }}>
                              {typeLabels[0]}
                            </span>
                          )
                        if (typeLabels.length === 2)
                          return (
                            <span style={{ fontSize: '14px' }}>
                              {typeLabels[0]} e {typeLabels[1]}
                            </span>
                          )

                        const lastType = typeLabels.pop()
                        return (
                          <span style={{ fontSize: '14px' }}>
                            {typeLabels.join(', ')} e {lastType}
                          </span>
                        )
                      })()}
                      <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
                        Qtd.:
                      </span>
                      <span style={{ fontSize: '14px' }}>
                        {item.quantity || 0}
                      </span>
                    </div>
                    <div style={{ fontSize: '20px', color: 'white' }}>
                      {collapsedItems[index] ? '▼' : '▶'}
                    </div>
                  </div>
                </ItemHeader>
                {!collapsedItems[index] && (
                  <ItemContent>
                    {item.itemType === 'MATERIAL' && (
                      <>
                        <h5>⚙️ Material</h5>
                        <InfoGrid>
                          {item.material && (
                            <InfoItem>
                              <InfoLabel>Nome:</InfoLabel>
                              <InfoValue>
                                {item.material.name || 'N/A'}
                              </InfoValue>
                            </InfoItem>
                          )}
                          {item.stock && (
                            <>
                              <InfoItem>
                                <InfoLabel>Estoque:</InfoLabel>
                                <InfoValue>
                                  {item.stock.description || 'N/A'}
                                </InfoValue>
                              </InfoItem>
                              {item.stock.code && (
                                <InfoItem>
                                  <InfoLabel>Código:</InfoLabel>
                                  <InfoValue>
                                    {item.stock.code.code || 'N/A'}
                                  </InfoValue>
                                </InfoItem>
                              )}
                              {item.stock.fabrication && (
                                <InfoItem>
                                  <InfoLabel>Fabricação:</InfoLabel>
                                  <InfoValue>
                                    {item.stock.fabrication.type || 'N/A'}
                                  </InfoValue>
                                </InfoItem>
                              )}
                              {item.stock.format && (
                                <InfoItem>
                                  <InfoLabel>Formato:</InfoLabel>
                                  <InfoValue>
                                    {item.stock.format.type || 'N/A'}
                                  </InfoValue>
                                </InfoItem>
                              )}
                              {item.stock.heatTreatment && (
                                <InfoItem>
                                  <InfoLabel>Tratamento Térmico:</InfoLabel>
                                  <InfoValue>
                                    {item.stock.heatTreatment.type || 'N/A'}
                                  </InfoValue>
                                </InfoItem>
                              )}
                              {item.stock.originCountry && (
                                <InfoItem>
                                  <InfoLabel>Origem:</InfoLabel>
                                  <InfoValue>
                                    {item.stock.originCountry.countryName ||
                                      'N/A'}
                                  </InfoValue>
                                </InfoItem>
                              )}
                              {item.stock.supplier && (
                                <InfoItem>
                                  <InfoLabel>Fornecedor:</InfoLabel>
                                  <InfoValue>
                                    {item.stock.supplier.companyName || 'N/A'}
                                  </InfoValue>
                                </InfoItem>
                              )}
                            </>
                          )}
                          <InfoItem>
                            <InfoLabel>Quantidade:</InfoLabel>
                            <InfoValue>{item.quantity || 'N/A'}</InfoValue>
                          </InfoItem>
                          {item.Attachments && item.Attachments.length > 0 && (
                            <InfoItem>
                              <InfoLabel>Arquivo:</InfoLabel>
                              <InfoValue>
                                {item.Attachments.map((a: any) => a.GeneralFile?.title || a.GeneralFile?.fileUrl || '').filter(Boolean).join(', ')}
                              </InfoValue>
                            </InfoItem>
                          )}
                          {item.extraInfo && (
                            <InfoItem>
                              <InfoLabel>Observações:</InfoLabel>
                              <InfoValue>{item.extraInfo}</InfoValue>
                            </InfoItem>
                          )}
                        </InfoGrid>
                      </>
                    )}

                    {item.itemType === 'SERVICE' && (
                      <>
                        <h5>🔧 Serviço</h5>
                        <InfoGrid>
                          {item.stage && (
                            <InfoItem>
                              <InfoLabel>Nome:</InfoLabel>
                              <InfoValue>{item.stage.name || 'N/A'}</InfoValue>
                            </InfoItem>
                          )}
                          {item.serviceCategory && (
                            <InfoItem>
                              <InfoLabel>Categoria:</InfoLabel>
                              <InfoValue>
                                {item.serviceCategory.name || 'N/A'}
                              </InfoValue>
                            </InfoItem>
                          )}
                          {item.serviceType && (
                            <InfoItem>
                              <InfoLabel>Tipo:</InfoLabel>
                              <InfoValue>
                                {item.serviceType.name || 'N/A'}
                              </InfoValue>
                            </InfoItem>
                          )}
                          {item.serviceSupplierCompany && (
                            <InfoItem>
                              <InfoLabel>Fornecedor:</InfoLabel>
                              <InfoValue>
                                {item.serviceSupplierCompany.companyName ||
                                  'N/A'}
                              </InfoValue>
                            </InfoItem>
                          )}
                          <InfoItem>
                            <InfoLabel>Quantidade:</InfoLabel>
                            <InfoValue>{item.quantity || 'N/A'}</InfoValue>
                          </InfoItem>
                          {item.Attachments && item.Attachments.length > 0 && (
                            <InfoItem>
                              <InfoLabel>Arquivo:</InfoLabel>
                              <InfoValue>
                                {item.Attachments.map((a: any) => a.GeneralFile?.title || a.GeneralFile?.fileUrl || '').filter(Boolean).join(', ')}
                              </InfoValue>
                            </InfoItem>
                          )}
                          {item.extraInfo && (
                            <InfoItem>
                              <InfoLabel>Observações:</InfoLabel>
                              <InfoValue>{item.extraInfo}</InfoValue>
                            </InfoItem>
                          )}
                        </InfoGrid>
                      </>
                    )}

                    {item.itemType === 'RESOURCE' && (
                      <>
                        <h5>🔧 Insumo</h5>
                        <InfoGrid>
                          {item.resource && (
                            <InfoItem>
                              <InfoLabel>Nome:</InfoLabel>
                              <InfoValue>
                                {item.resource.name || 'N/A'}
                              </InfoValue>
                            </InfoItem>
                          )}
                          {item.resourceCategory && (
                            <InfoItem>
                              <InfoLabel>Categoria:</InfoLabel>
                              <InfoValue>
                                {item.resourceCategory.name || 'N/A'}
                              </InfoValue>
                            </InfoItem>
                          )}
                          {item.resourceType && (
                            <InfoItem>
                              <InfoLabel>Tipo:</InfoLabel>
                              <InfoValue>
                                {item.resourceType.name || 'N/A'}
                              </InfoValue>
                            </InfoItem>
                          )}
                          {item.resourceSupplierCompany && (
                            <InfoItem>
                              <InfoLabel>Fornecedor:</InfoLabel>
                              <InfoValue>
                                {item.resourceSupplierCompany.companyName ||
                                  'N/A'}
                              </InfoValue>
                            </InfoItem>
                          )}
                          <InfoItem>
                            <InfoLabel>Quantidade:</InfoLabel>
                            <InfoValue>{item.quantity || 'N/A'}</InfoValue>
                          </InfoItem>
                          {item.Attachments && item.Attachments.length > 0 && (
                            <InfoItem>
                              <InfoLabel>Arquivo:</InfoLabel>
                              <InfoValue>
                                {item.Attachments.map((a: any) => a.GeneralFile?.title || a.GeneralFile?.fileUrl || '').filter(Boolean).join(', ')}
                              </InfoValue>
                            </InfoItem>
                          )}
                          {item.extraInfo && (
                            <InfoItem>
                              <InfoLabel>Observações:</InfoLabel>
                              <InfoValue>{item.extraInfo}</InfoValue>
                            </InfoItem>
                          )}
                        </InfoGrid>
                      </>
                    )}

                    {item.itemType === 'PRODUCT' && (
                      <>
                        <h5>📦 Produto</h5>
                        <InfoGrid>
                          {item.product && (
                            <InfoItem>
                              <InfoLabel>Nome:</InfoLabel>
                              <InfoValue>
                                {item.product.name || 'N/A'}
                              </InfoValue>
                            </InfoItem>
                          )}
                          {item.script && (
                            <InfoItem>
                              <InfoLabel>Script:</InfoLabel>
                              <InfoValue>{item.script.name || 'N/A'}</InfoValue>
                            </InfoItem>
                          )}
                          <InfoItem>
                            <InfoLabel>Quantidade:</InfoLabel>
                            <InfoValue>{item.quantity || 'N/A'}</InfoValue>
                          </InfoItem>
                          {item.Attachments && item.Attachments.length > 0 && (
                            <InfoItem>
                              <InfoLabel>Arquivo:</InfoLabel>
                              <InfoValue>
                                {item.Attachments.map((a: any) => a.GeneralFile?.title || a.GeneralFile?.fileUrl || '').filter(Boolean).join(', ')}
                              </InfoValue>
                            </InfoItem>
                          )}
                          {item.extraInfo && (
                            <InfoItem>
                              <InfoLabel>Observações:</InfoLabel>
                              <InfoValue>{item.extraInfo}</InfoValue>
                            </InfoItem>
                          )}
                        </InfoGrid>
                      </>
                    )}

                    {item.subItems && item.subItems.length > 0 && (
                      <>
                        {item.subItems.map((subItem: any, subIndex: number) => {
                          const getSubItemTypeLabel = () => {
                            const typeMap: { [key: string]: string } = {
                              MATERIAL: 'Material',
                              PRODUCT: 'Produto',
                              SERVICE: 'Serviço',
                              RESOURCE: 'Insumo',
                            }
                            return typeMap[subItem.itemType] || subItem.itemType
                          }

                          const getSubItemTitle = () => {
                            if (
                              subItem.itemType === 'MATERIAL' &&
                              subItem.material?.name
                            ) {
                              return subItem.material.name
                            }
                            if (
                              subItem.itemType === 'PRODUCT' &&
                              subItem.product?.name
                            ) {
                              return subItem.product.name
                            }
                            if (
                              subItem.itemType === 'SERVICE' &&
                              subItem.stage?.name
                            ) {
                              return subItem.stage.name
                            }
                            if (
                              subItem.itemType === 'RESOURCE' &&
                              subItem.resource?.name
                            ) {
                              return subItem.resource.name
                            }
                            return `${subItem.itemType} ${subIndex + 1}`
                          }

                          return (
                            <div
                              key={subIndex}
                              style={{
                                marginLeft: '20px',
                                borderLeft: '2px solid #e0e0e0',
                                paddingLeft: '15px',
                                marginTop: '15px',
                              }}
                            >
                              {subItem.itemType === 'MATERIAL' && (
                                <>
                                  <h5
                                    style={{
                                      marginBottom: '15px',
                                      color: '#333',
                                      fontSize: '16px',
                                    }}
                                  >
                                    ⚙️ {getSubItemTypeLabel()}
                                  </h5>
                                  <InfoGrid>
                                    {subItem.material && (
                                      <InfoItem>
                                        <InfoLabel>Nome:</InfoLabel>
                                        <InfoValue>
                                          {subItem.material.name || 'N/A'}
                                        </InfoValue>
                                      </InfoItem>
                                    )}
                                    {subItem.stock && (
                                      <>
                                        <InfoItem>
                                          <InfoLabel>Estoque:</InfoLabel>
                                          <InfoValue>
                                            {subItem.stock.description || 'N/A'}
                                          </InfoValue>
                                        </InfoItem>
                                        {subItem.stock.code && (
                                          <InfoItem>
                                            <InfoLabel>Código:</InfoLabel>
                                            <InfoValue>
                                              {subItem.stock.code.code || 'N/A'}
                                            </InfoValue>
                                          </InfoItem>
                                        )}
                                        {subItem.stock.fabrication && (
                                          <InfoItem>
                                            <InfoLabel>Fabricação:</InfoLabel>
                                            <InfoValue>
                                              {subItem.stock.fabrication.type ||
                                                'N/A'}
                                            </InfoValue>
                                          </InfoItem>
                                        )}
                                        {subItem.stock.format && (
                                          <InfoItem>
                                            <InfoLabel>Formato:</InfoLabel>
                                            <InfoValue>
                                              {subItem.stock.format.type ||
                                                'N/A'}
                                            </InfoValue>
                                          </InfoItem>
                                        )}
                                        {subItem.stock.heatTreatment && (
                                          <InfoItem>
                                            <InfoLabel>
                                              Tratamento Térmico:
                                            </InfoLabel>
                                            <InfoValue>
                                              {subItem.stock.heatTreatment
                                                .type || 'N/A'}
                                            </InfoValue>
                                          </InfoItem>
                                        )}
                                        {subItem.stock.originCountry && (
                                          <InfoItem>
                                            <InfoLabel>Origem:</InfoLabel>
                                            <InfoValue>
                                              {subItem.stock.originCountry
                                                .countryName || 'N/A'}
                                            </InfoValue>
                                          </InfoItem>
                                        )}
                                        {subItem.stock.supplier && (
                                          <InfoItem>
                                            <InfoLabel>Fornecedor:</InfoLabel>
                                            <InfoValue>
                                              {subItem.stock.supplier
                                                .companyName || 'N/A'}
                                            </InfoValue>
                                          </InfoItem>
                                        )}
                                      </>
                                    )}
                                    <InfoItem>
                                      <InfoLabel>Quantidade:</InfoLabel>
                                      <InfoValue>
                                        {subItem.quantity || 'N/A'}
                                      </InfoValue>
                                    </InfoItem>
                                    {subItem.Attachments &&
                                      subItem.Attachments.length > 0 && (
                                        <InfoItem>
                                          <InfoLabel>Arquivo:</InfoLabel>
                                          <InfoValue>
                                            {subItem.Attachments.map((a: any) => a.GeneralFile?.title || a.GeneralFile?.fileUrl || '').filter(Boolean).join(', ')}
                                          </InfoValue>
                                        </InfoItem>
                                      )}
                                    {subItem.extraInfo && (
                                      <InfoItem>
                                        <InfoLabel>Observações:</InfoLabel>
                                        <InfoValue>
                                          {subItem.extraInfo}
                                        </InfoValue>
                                      </InfoItem>
                                    )}
                                  </InfoGrid>
                                </>
                              )}

                              {subItem.itemType === 'SERVICE' && (
                                <>
                                  <h5
                                    style={{
                                      marginBottom: '15px',
                                      color: '#333',
                                      fontSize: '16px',
                                    }}
                                  >
                                    🔧 {getSubItemTypeLabel()}
                                  </h5>
                                  <InfoGrid>
                                    {subItem.stage && (
                                      <InfoItem>
                                        <InfoLabel>Nome:</InfoLabel>
                                        <InfoValue>
                                          {subItem.stage.name || 'N/A'}
                                        </InfoValue>
                                      </InfoItem>
                                    )}
                                    {subItem.serviceCategory && (
                                      <InfoItem>
                                        <InfoLabel>Categoria:</InfoLabel>
                                        <InfoValue>
                                          {subItem.serviceCategory.name ||
                                            'N/A'}
                                        </InfoValue>
                                      </InfoItem>
                                    )}
                                    {subItem.serviceType && (
                                      <InfoItem>
                                        <InfoLabel>Tipo:</InfoLabel>
                                        <InfoValue>
                                          {subItem.serviceType.name || 'N/A'}
                                        </InfoValue>
                                      </InfoItem>
                                    )}
                                    {subItem.serviceSupplierCompany && (
                                      <InfoItem>
                                        <InfoLabel>Fornecedor:</InfoLabel>
                                        <InfoValue>
                                          {subItem.serviceSupplierCompany
                                            .companyName || 'N/A'}
                                        </InfoValue>
                                      </InfoItem>
                                    )}
                                    <InfoItem>
                                      <InfoLabel>Quantidade:</InfoLabel>
                                      <InfoValue>
                                        {subItem.quantity || 'N/A'}
                                      </InfoValue>
                                    </InfoItem>
                                    {subItem.Attachments &&
                                      subItem.Attachments.length > 0 && (
                                        <InfoItem>
                                          <InfoLabel>Arquivo:</InfoLabel>
                                          <InfoValue>
                                            {subItem.Attachments.map((a: any) => a.GeneralFile?.title || a.GeneralFile?.fileUrl || '').filter(Boolean).join(', ')}
                                          </InfoValue>
                                        </InfoItem>
                                      )}
                                    {subItem.extraInfo && (
                                      <InfoItem>
                                        <InfoLabel>Observações:</InfoLabel>
                                        <InfoValue>
                                          {subItem.extraInfo}
                                        </InfoValue>
                                      </InfoItem>
                                    )}
                                  </InfoGrid>
                                </>
                              )}

                              {subItem.itemType === 'RESOURCE' && (
                                <>
                                  <h5
                                    style={{
                                      marginBottom: '15px',
                                      color: '#333',
                                      fontSize: '16px',
                                    }}
                                  >
                                    🛠️ {getSubItemTypeLabel()}
                                  </h5>
                                  <InfoGrid>
                                    {subItem.resource && (
                                      <InfoItem>
                                        <InfoLabel>Nome:</InfoLabel>
                                        <InfoValue>
                                          {subItem.resource.name || 'N/A'}
                                        </InfoValue>
                                      </InfoItem>
                                    )}
                                    {subItem.resourceCategory && (
                                      <InfoItem>
                                        <InfoLabel>Categoria:</InfoLabel>
                                        <InfoValue>
                                          {subItem.resourceCategory.name ||
                                            'N/A'}
                                        </InfoValue>
                                      </InfoItem>
                                    )}
                                    {subItem.resourceType && (
                                      <InfoItem>
                                        <InfoLabel>Tipo:</InfoLabel>
                                        <InfoValue>
                                          {subItem.resourceType.name || 'N/A'}
                                        </InfoValue>
                                      </InfoItem>
                                    )}
                                    {subItem.resourceSupplierCompany && (
                                      <InfoItem>
                                        <InfoLabel>Fornecedor:</InfoLabel>
                                        <InfoValue>
                                          {subItem.resourceSupplierCompany
                                            .companyName || 'N/A'}
                                        </InfoValue>
                                      </InfoItem>
                                    )}
                                    <InfoItem>
                                      <InfoLabel>Quantidade:</InfoLabel>
                                      <InfoValue>
                                        {subItem.quantity || 'N/A'}
                                      </InfoValue>
                                    </InfoItem>
                                    {subItem.Attachments &&
                                      subItem.Attachments.length > 0 && (
                                        <InfoItem>
                                          <InfoLabel>Arquivo:</InfoLabel>
                                          <InfoValue>
                                            {subItem.Attachments.map((a: any) => a.GeneralFile?.title || a.GeneralFile?.fileUrl || '').filter(Boolean).join(', ')}
                                          </InfoValue>
                                        </InfoItem>
                                      )}
                                    {subItem.extraInfo && (
                                      <InfoItem>
                                        <InfoLabel>Observações:</InfoLabel>
                                        <InfoValue>
                                          {subItem.extraInfo}
                                        </InfoValue>
                                      </InfoItem>
                                    )}
                                  </InfoGrid>
                                </>
                              )}

                              {subItem.itemType === 'PRODUCT' && (
                                <>
                                  <h5
                                    style={{
                                      marginBottom: '15px',
                                      color: '#333',
                                      fontSize: '16px',
                                    }}
                                  >
                                    📦 {getSubItemTypeLabel()}
                                  </h5>
                                  <InfoGrid>
                                    {subItem.product && (
                                      <InfoItem>
                                        <InfoLabel>Nome:</InfoLabel>
                                        <InfoValue>
                                          {subItem.product.name || 'N/A'}
                                        </InfoValue>
                                      </InfoItem>
                                    )}
                                    {subItem.script && (
                                      <InfoItem>
                                        <InfoLabel>Script:</InfoLabel>
                                        <InfoValue>
                                          {subItem.script.name || 'N/A'}
                                        </InfoValue>
                                      </InfoItem>
                                    )}
                                    <InfoItem>
                                      <InfoLabel>Quantidade:</InfoLabel>
                                      <InfoValue>
                                        {subItem.quantity || 'N/A'}
                                      </InfoValue>
                                    </InfoItem>
                                    {subItem.Attachments &&
                                      subItem.Attachments.length > 0 && (
                                        <InfoItem>
                                          <InfoLabel>Arquivo:</InfoLabel>
                                          <InfoValue>
                                            {subItem.Attachments.map((a: any) => a.GeneralFile?.title || a.GeneralFile?.fileUrl || '').filter(Boolean).join(', ')}
                                          </InfoValue>
                                        </InfoItem>
                                      )}
                                    {subItem.extraInfo && (
                                      <InfoItem>
                                        <InfoLabel>Observações:</InfoLabel>
                                        <InfoValue>
                                          {subItem.extraInfo}
                                        </InfoValue>
                                      </InfoItem>
                                    )}
                                  </InfoGrid>
                                </>
                              )}
                            </div>
                          )
                        })}
                      </>
                    )}

                    <h5>💰 Subtotal</h5>
                    <InfoGrid>
                      <InfoItem>
                        <InfoLabel>Preço por Kg (Mín):</InfoLabel>
                        <InfoValue>
                          {formatCurrency(item.priceKgMin || 0)}
                        </InfoValue>
                      </InfoItem>
                      <InfoItem>
                        <InfoLabel>Preço por Kg (Final):</InfoLabel>
                        <InfoValue>
                          {formatCurrency(item.priceKg || 0)}
                        </InfoValue>
                      </InfoItem>
                      <InfoItem>
                        <InfoLabel>Desconto:</InfoLabel>
                        <InfoValue>
                          {item.descount ? `${item.descount}%` : '0%'}
                        </InfoValue>
                      </InfoItem>
                      <InfoItem>
                        <InfoLabel>Prazo de Entrega:</InfoLabel>
                        <InfoValue>{item.time || 'N/A'}</InfoValue>
                      </InfoItem>
                    </InfoGrid>
                  </ItemContent>
                )}
              </ItemCard>
            )
          })}

          <SectionTitle>Resumo Financeiro</SectionTitle>
          <InfoGrid>
            <InfoItem>
              <InfoLabel>Quantidade Total:</InfoLabel>
              <InfoValue>{order?.totalQuantity || 0}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Impostos:</InfoLabel>
              <InfoValue>{formatCurrency(order?.taxes)}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Total Geral:</InfoLabel>
              <InfoValue className='total-value'>
                {formatCurrency(order?.total)}
              </InfoValue>
            </InfoItem>
          </InfoGrid>

          {Array.isArray((order as any)?.OrderInstallment) &&
            (order as any).OrderInstallment.length > 0 && (
              <div style={{ marginTop: '24px' }}>
                <InfoLabel style={{ display: 'block', marginBottom: '16px' }}>
                  Parcelas
                </InfoLabel>
                <Table>
                  <thead>
                    <TableRow>
                      <TableHeaderCell width="64px">Nº</TableHeaderCell>
                      <TableHeaderCell>Valor</TableHeaderCell>
                      <TableHeaderCell>Vencimento</TableHeaderCell>
                      <TableHeaderCell width="160px">Comissão</TableHeaderCell>
                    </TableRow>
                  </thead>
                  <tbody>
                    {(order as any).OrderInstallment.map((parcela: any, index: number) => (
                      <TableRow key={parcela.id ?? index}>
                        <TableCell>{parcela.numero ?? index + 1}</TableCell>
                        <TableCell>{formatCurrency(Number(parcela.valor) || 0)}</TableCell>
                        <TableCell>{formatDate(parcela.vencimento)}</TableCell>
                        <TableCell>
                          {parcela.comissaoAtiva
                            ? `Sim${parcela.comissaoPercentual ? ` (${Number(parcela.comissaoPercentual)}%)` : ''}`
                            : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
        </ScrollableContent>
      </DocumentContainer>
    </ModalOverlay>
  )
}

export default ViewSteelOrder
